import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import baseURL from '../../url';
import './BulkProductImport.css';

const SAMPLE_ROWS = [
    {
        Titulo: 'Remera clasica',
        SKU: 'REM-001',
        Marca: 'Tu marca',
        Descripcion: 'Remera de algodon lista para carga masiva',
        Precio: 39,
        PrecioAnterior: 49,
        Categoria: 'Productos importados',
        MasVendido: 'si',
        Stock: 30,
        Imagen1: 'https://images.example.com/remera-clasica-frente.jpg',
        Imagen2: 'https://images.example.com/remera-clasica-dorso.jpg',
        Item1: 'S',
        ItemStock1: 10,
        Item2: 'M',
        ItemStock2: 10,
        Item3: 'L',
        ItemStock3: 10,
    },
];

export default function BulkProductImport({ onImported }) {
    const inputRef = useRef(null);
    const [parsedRows, setParsedRows] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusTone, setStatusTone] = useState('info');

    const updateStatus = (message, tone = 'info') => {
        setStatusMessage(message);
        setStatusTone(tone);
    };

    const resetSelection = (clearStatus = true) => {
        setParsedRows([]);
        if (clearStatus) {
            setStatusMessage('');
            setStatusTone('info');
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    const handleOpenPicker = () => {
        inputRef.current?.click();
    };

    const handleDownloadTemplate = () => {
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(SAMPLE_ROWS);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Productos');
        XLSX.writeFile(workbook, 'plantilla-carga-masiva-productos.xlsx');
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

            if (!rows.length) {
                updateStatus('El archivo Excel esta vacio.', 'error');
                resetSelection(false);
                return;
            }

            setParsedRows(rows);
            updateStatus(`${rows.length} fila(s) listas para importar.`, 'success');
        } catch (error) {
            console.error(error);
            updateStatus('No se pudo leer el archivo Excel.', 'error');
            resetSelection(false);
        }
    };

    const handleImport = async () => {
        if (!parsedRows.length) {
            updateStatus('Primero selecciona un archivo Excel.', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(`${baseURL}/productosBulkPost.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    rows: parsedRows,
                }),
            });

            const data = await response.json();

            if (data.error) {
                if (Array.isArray(data.details) && data.details.length) {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Importacion bloqueada',
                        html: data.details
                            .slice(0, 12)
                            .map((detail) => `<div style="text-align:left;">${detail}</div>`)
                            .join(''),
                    });
                } else {
                    await Swal.fire({
                        icon: 'error',
                        title: 'Importacion fallida',
                        text: data.error,
                    });
                }
                updateStatus(data.error || 'La importacion no se pudo completar.', 'error');
                return;
            }

            updateStatus(data.mensaje || 'Productos importados correctamente.', 'success');
            resetSelection(false);
            onImported?.();
        } catch (error) {
            console.error(error);
            updateStatus('La importacion fallo. Intenta nuevamente.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='bulkImportCard'>
            <div className='bulkImportTop'>
                <div>
                    <h3>Carga masiva</h3>
                    <p>Sube archivos `.xlsx` o `.xls` con productos, SKU, marca, categorias, stock e imagenes por URL opcionales. Las categorias que no existan se crean automaticamente.</p>
                    {statusMessage && (
                        <p className={`bulkImportStatus is-${statusTone}`}>{statusMessage}</p>
                    )}
                </div>
                <div className='bulkImportActions'>
                    <button type='button' className='ghostActionButton' onClick={handleDownloadTemplate}>
                        Plantilla
                    </button>
                    <button type='button' className='ghostActionButton' onClick={handleOpenPicker}>
                        Seleccionar Excel
                    </button>
                    <button type='button' className='primaryActionButton' onClick={handleImport} disabled={isSubmitting || !parsedRows.length}>
                        {isSubmitting ? 'Importando...' : 'Importar productos'}
                    </button>
                </div>
            </div>

            <input
                ref={inputRef}
                type='file'
                accept='.xlsx,.xls'
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
        </div>
    );
}
